import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { getAppPortalRoot } from "../utils/appPortal";
import { getAppScale } from "../utils/appScale";
import "./CpanelYesNoDropdown.css";

const MENU_GAP = 4;
const VIEWPORT_PADDING = 8;

const DEFAULT_OPTIONS = [
  { value: true, label: "Oui" },
  { value: false, label: "Non" },
];

export default function CpanelYesNoDropdown({
  value,
  onChange,
  disabled = false,
  ariaLabel = "Oui ou non",
  options = DEFAULT_OPTIONS,
}) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const selected = options.find((option) => option.value === value) || options[0];

  const updateMenuPosition = useCallback(() => {
    const triggerEl = triggerRef.current;
    if (!triggerEl) return;
    const scale = getAppScale();
    const rect = triggerEl.getBoundingClientRect();
    const menuHeight = (menuRef.current?.offsetHeight || 72) * scale;
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
    const spaceAbove = rect.top - VIEWPORT_PADDING;
    const openAbove = spaceBelow < menuHeight && spaceAbove >= spaceBelow;
    const width = rect.width / scale;
    const style = {
      left: rect.left,
      width,
      minWidth: width,
      maxWidth: width,
      transformOrigin: openAbove ? "bottom left" : "top left",
      transform:
        scale === 1
          ? openAbove
            ? "translateY(-100%)"
            : "none"
          : openAbove
            ? `translateY(-100%) scale(${scale})`
            : `scale(${scale})`,
    };
    if (openAbove) {
      style.top = rect.top - MENU_GAP;
    } else {
      style.top = rect.bottom + MENU_GAP;
    }
    setMenuStyle(style);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const frame = requestAnimationFrame(updateMenuPosition);
    return () => cancelAnimationFrame(frame);
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown, true);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown, true);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  return (
    <div
      ref={rootRef}
      className={[
        "cpanel-yesno-dropdown",
        open && "cpanel-yesno-dropdown--open",
        disabled && "cpanel-yesno-dropdown--disabled",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <button
        ref={triggerRef}
        type="button"
        className="cpanel-yesno-dropdown__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen((current) => !current);
        }}
      >
        <span className="cpanel-yesno-dropdown__label">{selected?.label}</span>
        <ChevronDown
          size={12}
          strokeWidth={2}
          className="cpanel-yesno-dropdown__chevron"
          aria-hidden="true"
        />
      </button>
      {open && !disabled
        ? createPortal(
            <ul
              ref={menuRef}
              className="cpanel-yesno-dropdown__menu"
              style={menuStyle || undefined}
              role="listbox"
            >
              {options.map((option) => (
                <li key={String(option.value)} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    className={[
                      "cpanel-yesno-dropdown__option",
                      option.value === value && "cpanel-yesno-dropdown__option--selected",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => {
                      setOpen(false);
                      if (option.value !== value) onChange(option.value);
                    }}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>,
            getAppPortalRoot()
          )
        : null}
    </div>
  );
}
